import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Input, List, Card, Tag, Spin, Empty, Button, Divider } from 'antd';
import type { InputRef } from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  EnvironmentOutlined,
  ApiOutlined,
  ClockCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { searchApi, SearchResult } from '@/api/searchApi';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import { useSearchHistory } from '@shared/hooks/useSearchHistory';

export function GlobalSearch() {
  const { t } = useTranslation(['translation']);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);

  // История поиска
  const { history, addToHistory, clearHistory } = useSearchHistory();

  // Debounce поискового запроса (300мс задержка)
  const debouncedSearch = useDebouncedValue(searchValue, 300);

  // Глобальный поиск через API
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['global-search', debouncedSearch],
    queryFn: async () => {
      const result = await searchApi.globalSearch({ q: debouncedSearch, limit: 10 });
      return result;
    },
    enabled: debouncedSearch.length >= 3, // Запускать только если >= 3 символа
    staleTime: 5000, // Кэшировать на 5 секунд
    retry: false, // Не повторять при ошибке
  });

  const searchResults = useMemo(() => data?.results ?? [], [data]);

  // Закрытие dropdown при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsDropdownVisible(false);
        setSelectedIndex(0);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setIsDropdownVisible(true);
    setSelectedIndex(0);
  };

  const handleInputFocus = () => {
    if (searchValue.length > 0 || history.length > 0) {
      setIsDropdownVisible(true);
    }
  };

  const handleResultClick = useCallback(
    (item: SearchResult) => {
      navigate(item.url);
      addToHistory(searchValue);
      setIsDropdownVisible(false);
      setSearchValue('');
      setSelectedIndex(0);
    },
    [navigate, addToHistory, searchValue],
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isDropdownVisible) return;

      const totalItems = searchResults.length + history.length;
      if (totalItems === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % totalItems);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + totalItems) % totalItems);
          break;
        case 'Enter':
          e.preventDefault();
          if (searchValue.length >= 3 && searchResults.length > 0) {
            // Если есть результаты поиска - переходим на выбранный
            if (selectedIndex < searchResults.length) {
              handleResultClick(searchResults[selectedIndex]);
            }
          } else if (history.length > 0 && selectedIndex < history.length) {
            // Если показываем историю - используем выбранный запрос
            setSearchValue(history[selectedIndex]);
            inputRef.current?.focus();
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsDropdownVisible(false);
          setSelectedIndex(0);
          inputRef.current?.blur();
          break;
      }
    };

    if (isDropdownVisible) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isDropdownVisible, searchResults, selectedIndex, searchValue, history, handleResultClick]);

  const handleHistoryClick = (query: string) => {
    setSearchValue(query);
    inputRef.current?.focus();
  };

  const handleShowAllResults = () => {
    if (searchValue.trim()) {
      navigate(`/customers?search=${encodeURIComponent(searchValue)}`);
      addToHistory(searchValue);
      setIsDropdownVisible(false);
      setSearchValue('');
      setSelectedIndex(0);
    }
  };

  const getTypeIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'customer':
        return <UserOutlined className="text-blue-500" />;
      case 'building':
        return <EnvironmentOutlined className="text-orange-500" />;
      case 'device':
        return <ApiOutlined className="text-green-500" />;
      default:
        return <SearchOutlined />;
    }
  };

  const getTypeTag = (type: SearchResult['type']) => {
    switch (type) {
      case 'customer':
        return <Tag color="blue">{t('search.type.customer') || 'Абонент'}</Tag>;
      case 'building':
        return <Tag color="orange">{t('search.type.building') || 'Дом'}</Tag>;
      case 'device':
        return <Tag color="green">{t('search.type.device') || 'Устройство'}</Tag>;
      default:
        return null;
    }
  };

  // Группировка результатов по типам
  const groupedResults = searchResults.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {} as Record<SearchResult['type'], SearchResult[]>);

  const showHistory = searchValue.length === 0 && history.length > 0;
  const showSearchResults = searchValue.length >= 3;

  return (
    <div ref={searchRef} style={{ position: 'relative', width: '100%', maxWidth: '500px' }}>
      <Input
        ref={inputRef}
        placeholder={t('search.placeholder') || 'Поиск абонентов, домов, устройств...'}
        prefix={<SearchOutlined />}
        suffix={
          isFetching ? (
            <Spin size="small" />
          ) : undefined
        }
        value={searchValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        allowClear
        onClear={() => {
          setIsDropdownVisible(false);
          setSelectedIndex(0);
        }}
        size="large"
      />

      {/* Dropdown с результатами */}
      {isDropdownVisible && (
        <Card
          style={{
            position: 'absolute',
            top: '50px',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '500px',
            overflow: 'auto',
            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
          }}
          styles={{ body: { padding: 0 } }}
        >
          {/* История поиска */}
          {showHistory && (
            <div className="py-2">
              <div className="flex items-center justify-between px-4 py-2">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t('search.history') || 'История поиска'}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={clearHistory}
                >
                  {t('common.clear') || 'Очистить'}
                </Button>
              </div>
              <List
                dataSource={history}
                renderItem={(item, index) => (
                  <List.Item
                    onClick={() => handleHistoryClick(item)}
                    className={`cursor-pointer transition-colors px-4 ${
                      index === selectedIndex
                        ? 'bg-blue-50 dark:bg-blue-900'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    style={{ cursor: 'pointer' }}
                  >
                    <List.Item.Meta
                      avatar={<ClockCircleOutlined className="text-gray-400" />}
                      title={<span className="text-gray-700 dark:text-gray-300">{item}</span>}
                    />
                  </List.Item>
                )}
              />
            </div>
          )}

          {/* Подсказка - минимум 3 символа */}
          {searchValue.length > 0 && searchValue.length < 3 && (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              {t('search.min_chars') || 'Введите минимум 3 символа для поиска...'}
            </div>
          )}

          {/* Loading состояние */}
          {showSearchResults && isLoading && (
            <div className="p-8 text-center">
              <Spin size="large" />
              <div className="mt-4 text-gray-500 dark:text-gray-400">
                {t('search.searching') || 'Поиск...'}
              </div>
            </div>
          )}

          {/* Результаты поиска */}
          {showSearchResults && !isLoading && (
            <>
              {searchResults.length > 0 ? (
                <div className="py-2">
                  {/* Группировка по типам */}
                  {Object.entries(groupedResults).map(([type, results]) => (
                    <div key={type}>
                      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                          {type === 'customer' && (t('search.type.customers') || 'Абоненты')}
                          {type === 'building' && (t('search.type.buildings') || 'Дома')}
                          {type === 'device' && (t('search.type.devices') || 'Устройства')}
                          {' '}({results.length})
                        </span>
                      </div>
                      <List
                        dataSource={results}
                        renderItem={(item) => {
                          const globalIndex = searchResults.indexOf(item);
                          return (
                            <List.Item
                              onClick={() => handleResultClick(item)}
                              className={`cursor-pointer transition-colors px-4 ${
                                globalIndex === selectedIndex
                                  ? 'bg-blue-50 dark:bg-blue-900'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                              style={{ cursor: 'pointer' }}
                            >
                              <List.Item.Meta
                                avatar={getTypeIcon(item.type)}
                                title={
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900 dark:text-white">
                                      {item.title}
                                    </span>
                                    {getTypeTag(item.type)}
                                  </div>
                                }
                                description={
                                  <span className="text-gray-600 dark:text-gray-400">
                                    {item.subtitle}
                                  </span>
                                }
                              />
                            </List.Item>
                          );
                        }}
                      />
                      {Object.keys(groupedResults).indexOf(type) < Object.keys(groupedResults).length - 1 && (
                        <Divider style={{ margin: 0 }} />
                      )}
                    </div>
                  ))}

                  {/* Показать все результаты */}
                  <div
                    className="p-3 text-center bg-gray-50 dark:bg-gray-800 border-t dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={handleShowAllResults}
                  >
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {t('search.show_all') || 'Показать все результаты'} →
                    </span>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('search.no_results') || 'Ничего не найдено'}
                      </span>
                    }
                  >
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      {t('search.try_another') || 'Попробуйте изменить запрос'}
                    </div>
                  </Empty>
                </div>
              )}
            </>
          )}

          {/* Подсказки для пустого поиска */}
          {searchValue.length === 0 && history.length === 0 && (
            <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="mb-2 font-semibold">{t('search.tips.title') || 'Подсказки:'}</div>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('search.tips.customer') || 'Ищите по ФИО, логину, телефону, email'}</li>
                <li>{t('search.tips.building') || 'Ищите дома по адресу'}</li>
                <li>{t('search.tips.device') || 'Ищите устройства по имени или IP'}</li>
              </ul>
              <div className="mt-3 text-xs text-gray-400">
                {t('search.tips.keyboard') || 'Используйте ↑↓ для навигации, Enter для выбора'}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
